import Janus from 'janus-gateway';

let janusReady = false;

function ensureJanus (onReady, onError) {
	if (janusReady) {
		onReady();
		return;
	}
	if (!Janus.isWebrtcSupported()) {
		onError('WebRTC is not supported in this browser.');
		return;
	}
	Janus.init({
		debug: process.env.NODE_ENV === 'development' ? 'all' : false,
		callback: () => {
			janusReady = true;
			onReady();
		},
	});
}

export const CONSULT_DISPLAY_EXAM = 'consult-exam';
export const CONSULT_DISPLAY_PATIENT = 'consult-patient';
export const CONSULT_DISPLAY_CLINICIAN = 'Clinician';

/**
 * Clinician-side Janus VideoRoom client: publish webcam+audio, subscribe to
 * operator feeds (UVC + patient face).
 */
export class VideoConsultClient {
	/**
	 * @param {(level: string, line: string) => void} onLog
	 * @param {(connected: boolean) => void} onPublisherState
	 */
	constructor (onLog, onPublisherState) {
		this.onLog = onLog;
		this.onPublisherState = onPublisherState;
		this.onRemoteStreams = null;
		this.janus = null;
		this.publisherHandle = null;
		/** @type {Map<string, { handle: object, stream: MediaStream, display: string, tracksByMid: Map }>} */
		this.remoteFeeds = new Map();
		/**
		 * Feed ids whose `attach` is in flight. Prevents a second `publishers`
		 * event (or the joined+event interleave on web reload) from creating
		 * a duplicate subscriber handle that would overwrite the first entry
		 * in `remoteFeeds` and orphan the original `onremotetrack` stream.
		 * @type {Set<string>}
		 */
		this.pendingSubscriptions = new Set();
		this.roomId = null;
		this.displayName = CONSULT_DISPLAY_CLINICIAN;
		this.localStream = null;
		this.tearingDown = false;
	}

	connect (serverUrl, roomId, displayName) {
		this.disconnect();
		this.roomId = roomId;
		this.displayName = displayName || CONSULT_DISPLAY_CLINICIAN;

		ensureJanus(
			() => {
				this.janus = new Janus({
					server: serverUrl,
					success: () => {
						this.onLog('info', 'Janus session created.');
						this.attachPublisher();
					},
					error: (err) => this.onLog('error', `Janus error: ${err}`),
					destroyed: () => this.onLog('info', 'Janus session destroyed.'),
				});
			},
			(msg) => this.onLog('error', msg),
		);
	}

	disconnect () {
		this.tearingDown = true;
		const localStream = this.localStream;
		this.localStream = null;
		this.remoteFeeds.forEach((entry) => {
			try {
				if (entry.handle) entry.handle.detach();
			} catch {
				/* ignore */
			}
		});
		this.remoteFeeds.clear();
		this.pendingSubscriptions.clear();
		if (this.publisherHandle) {
			try {
				this.publisherHandle.hangup();
				this.publisherHandle.detach();
			} catch {
				/* ignore */
			}
			this.publisherHandle = null;
		}
		if (localStream) localStream.getTracks().forEach((t) => t.stop());
		if (this.janus) {
			this.janus.destroy();
			this.janus = null;
		}
		this.tearingDown = false;
		this.onPublisherState(false);
	}

	attachPublisher () {
		this.janus.attach({
			plugin: 'janus.plugin.videoroom',
			opaqueId: 'consult-clinician',
			success: (handle) => {
				this.publisherHandle = handle;
				this.onLog('info', 'Publisher handle attached.');
				// janus.js's send().success is just an ACK that the request was
				// accepted, not the actual VideoRoom joined response. The "joined"
				// event arrives asynchronously via `onmessage`, so we wait for it
				// there before publishing local media.
				handle.send({
					message: {
						request: 'join',
						room: this.roomId,
						ptype: 'publisher',
						display: this.displayName,
					},
					error: (err) => this.onLog('error', `Join request failed: ${err}`),
				});
			},
			error: (err) => this.onLog('error', `Attach failed: ${err}`),
			onmessage: (msg, jsep) => {
				const event = msg?.videoroom;
				if (event === 'joined') {
					const summary = (msg.publishers || []).map((p) => `${p.display || '?'}#${p.id}`).join(', ');
					this.onLog('info', `Joined room ${msg.room} as id ${msg.id}; existing publishers: [${summary || 'none'}]`);
					if (Array.isArray(msg.publishers) && msg.publishers.length) {
						this.subscribeToRemotes(msg.publishers);
					}
					this.publishLocalMedia();
				} else if (event === 'event') {
					if (Array.isArray(msg.publishers) && msg.publishers.length) {
						const summary = msg.publishers.map((p) => `${p.display || '?'}#${p.id}`).join(', ');
						this.onLog('info', `New publishers: [${summary}]`);
						this.subscribeToRemotes(msg.publishers);
					}
					if (msg.leaving) {
						this.onLog('info', `Publisher leaving: ${msg.leaving}`);
						this.removeRemote(msg.leaving);
					}
					if (msg.unpublished) {
						this.onLog('info', `Publisher unpublished: ${msg.unpublished}`);
						this.removeRemote(msg.unpublished);
					}
				}
				if (jsep && this.publisherHandle) {
					this.publisherHandle.handleRemoteJsep({ jsep });
				}
			},
			onlocalstream: (stream) => {
				this.localStream = stream;
				this.onPublisherState(true, stream);
			},
			oncleanup: () => {
				this.localStream = null;
				this.onPublisherState(false, null);
				// Janus stopped our tracks (ICE failure, not an intentional
				// disconnect). If the session is still alive, reacquire the camera
				// so the clinician's self-preview stays visible.
				if (this.janus && !this.tearingDown) {
					navigator.mediaDevices
						.getUserMedia({ audio: false, video: true })
						.then((stream) => {
							if (!this.janus || this.tearingDown) {
								stream.getTracks().forEach((t) => t.stop());
								return;
							}
							this.localStream = stream;
							this.onPublisherState(false, stream);
						})
						.catch(() => {});
				}
			},
		});
	}

	publishLocalMedia () {
		const handle = this.publisherHandle;
		if (!handle) return;
		navigator.mediaDevices
			.getUserMedia({ audio: true, video: true })
			.then((stream) => {
				if (this.publisherHandle !== handle) {
					stream.getTracks().forEach((t) => t.stop());
					return;
				}
				this.localStream = stream;
				// Surface the local stream immediately so the clinician sees
				// their own webcam even if Janus signaling later fails. We call
				// onPublisherState with the stream so the composable updates
				// localStream regardless of createOffer's outcome.
				this.onPublisherState(true, stream);
				// janus-gateway 1.4.x deprecates the `media`/`stream` form of
				// createOffer and only honors the multistream `tracks` array. We
				// pass our already-captured MediaStreamTracks via `capture` so
				// the library doesn't re-open the camera/mic a second time.
				const audioTrack = stream.getAudioTracks()[0];
				const videoTrack = stream.getVideoTracks()[0];
				const tracks = [];
				if (audioTrack) {
					tracks.push({ type: 'audio', capture: audioTrack, recv: false });
				}
				if (videoTrack) {
					tracks.push({ type: 'video', capture: videoTrack, recv: false });
				}
				handle.createOffer({
					tracks,
					success: (jsep) => {
						handle.send({
							message: { request: 'configure', audio: true, video: true },
							jsep,
						});
						this.onLog('info', 'Publishing clinician A/V.');
					},
					error: (err) => this.onLog('error', `Offer failed: ${err}`),
				});
			})
			.catch((err) => this.onLog('error', `getUserMedia: ${err.message}`));
	}

	/**
	 * Toggle the local outgoing video track on/off. Mirrors the Android
	 * `ConsultPublisher.setVideoEnabled(...)` call: flips the MediaStreamTrack's
	 * `.enabled` flag, which keeps the SDP m-line in place (no renegotiation)
	 * but stops transmitting frames so the remote sees a frozen / black tile.
	 * @returns {boolean} the new enabled state, or the previous one if no track.
	 */
	setVideoEnabled (enabled) {
		const track = this.localStream?.getVideoTracks?.()?.[0];
		if (!track) return false;
		track.enabled = !!enabled;
		this.onLog('info', `Local video ${track.enabled ? 'enabled' : 'disabled'}.`);
		return track.enabled;
	}

	/** Mute / unmute the outgoing microphone (audio track .enabled flag). */
	setAudioEnabled (enabled) {
		const track = this.localStream?.getAudioTracks?.()?.[0];
		if (!track) return false;
		track.enabled = !!enabled;
		this.onLog('info', `Local mic ${track.enabled ? 'enabled' : 'muted'}.`);
		return track.enabled;
	}

	/**
	 * Switch the publishing webcam to the next enumerated video input device.
	 * Uses `RTCRtpSender.replaceTrack` so no renegotiation is required — the
	 * existing PeerConnection / SDP stays intact. Falls back to a no-op if
	 * the host only exposes a single camera (e.g. desktop with one webcam).
	 *
	 * @returns {Promise<{ok: boolean, deviceId?: string, label?: string, reason?: string}>}
	 */
	async switchCamera () {
		if (!this.localStream) {
			return { ok: false, reason: 'no-local-stream' };
		}
		const current = this.localStream.getVideoTracks()[0];
		if (!current) return { ok: false, reason: 'no-video-track' };
		let devices;
		try {
			devices = await navigator.mediaDevices.enumerateDevices();
		} catch (err) {
			this.onLog('error', `enumerateDevices: ${err?.message ?? err}`);
			return { ok: false, reason: 'enumerate-failed' };
		}
		const cams = devices.filter((d) => d.kind === 'videoinput');
		if (cams.length < 2) {
			this.onLog('warn', `Only ${cams.length} camera(s) available.`);
			return { ok: false, reason: 'single-camera' };
		}
		const currentId = current.getSettings?.().deviceId;
		const currentIdx = cams.findIndex((d) => d.deviceId === currentId);
		const next = cams[(currentIdx + 1) % cams.length];
		let newStream;
		try {
			newStream = await navigator.mediaDevices.getUserMedia({
				video: { deviceId: { exact: next.deviceId } },
				audio: false,
			});
		} catch (err) {
			this.onLog('error', `getUserMedia(next cam): ${err?.message ?? err}`);
			return { ok: false, reason: 'gum-failed' };
		}
		if (!this.janus || !this.localStream) {
			newStream.getTracks().forEach((t) => t.stop());
			return { ok: false, reason: 'disconnected' };
		}
		const newTrack = newStream.getVideoTracks()[0];
		if (!newTrack) {
			newStream.getTracks().forEach((t) => t.stop());
			return { ok: false, reason: 'no-new-track' };
		}
		// Replace the track on the active publisher's RTCRtpSender — this is
		// the recommended path for in-call camera switching (no SDP rebuild).
		const pc = this.publisherHandle?.webrtcStuff?.pc;
		const sender = pc?.getSenders?.().find((s) => s.track?.kind === 'video');
		if (sender) {
			try {
				await sender.replaceTrack(newTrack);
			} catch (err) {
				this.onLog('error', `replaceTrack: ${err?.message ?? err}`);
				newTrack.stop();
				return { ok: false, reason: 'replace-failed' };
			}
		} else {
			this.onLog('warn', 'No active video sender; new track will only show in local preview.');
		}
		// Swap the track inside the existing localStream so the bound <video>
		// element refreshes. We also stop the previous track to free the
		// camera device.
		try {
			current.stop();
			this.localStream.removeTrack(current);
			this.localStream.addTrack(newTrack);
		} catch (err) {
			this.onLog('warn', `localStream swap: ${err?.message ?? err}`);
		}
		// Notify subscribers (composable / view) with a fresh MediaStream so
		// the watcher in ConsultView triggers a re-bind on the <video> tag.
		const refreshed = new MediaStream(this.localStream.getTracks());
		this.localStream = refreshed;
		this.onPublisherState(true, refreshed);
		this.onLog('info', `Switched camera to ${next.label || next.deviceId}.`);
		return { ok: true, deviceId: next.deviceId, label: next.label };
	}

	subscribeToRemotes (publishers) {
		for (const pub of publishers) {
			const id = String(pub.id);
			const display = pub.display || '';
			if (display === CONSULT_DISPLAY_CLINICIAN) continue;
			if (display !== CONSULT_DISPLAY_EXAM && display !== CONSULT_DISPLAY_PATIENT) {
				continue;
			}
			// Skip if already subscribed OR if an attach is in flight for this
			// feed (otherwise the second pass would create a duplicate handle
			// that overwrites the first entry and orphans its track stream).
			if (this.remoteFeeds.has(id) || this.pendingSubscriptions.has(id)) {
				continue;
			}
			this.attachSubscriber(id, display);
		}
	}

	attachSubscriber (feedId, display) {
		const key = String(feedId);
		this.pendingSubscriptions.add(key);
		this.onLog('info', `Subscribing to ${display} (feed ${feedId})…`);
		// Captured in the attach callbacks so that oncleanup can tell whether
		// it belongs to *this* subscriber instance vs. a stale one (e.g. a
		// previous session's handle finishing teardown after we've already
		// re-subscribed to the same feed id with a fresh handle).
		let attachedHandle = null;
		this.janus.attach({
			plugin: 'janus.plugin.videoroom',
			opaqueId: `consult-sub-${feedId}`,
			success: (handle) => {
				attachedHandle = handle;
				// If we were disconnected (or detached this entry) while the
				// attach was in flight, drop the handle now to avoid leaking it.
				if (!this.janus || !this.pendingSubscriptions.has(key)) {
					try {
						handle.detach();
					} catch {
						/* ignore */
					}
					return;
				}
				this.pendingSubscriptions.delete(key);
				// Pre-register the entry with an empty MediaStream so onremotetrack
				// (which can fire before we'd otherwise know about the feed) can
				// append tracks. The stream is bound to the <video> element by the
				// composable; the element starts rendering as soon as a video
				// track is added to it.
				this.remoteFeeds.set(key, {
					handle,
					stream: new MediaStream(),
					display,
					tracksByMid: new Map(),
				});
				// Use the multistream subscriber API. The legacy `feed` form is
				// deprecated and janus.js 1.4.x only fires `onremotetrack` (not
				// `onremotestream`) for the resulting subscription.
				handle.send({
					message: {
						request: 'join',
						room: this.roomId,
						ptype: 'subscriber',
						streams: [{ feed: Number(feedId) }],
					},
					error: (err) => this.onLog('error', `Subscribe join failed: ${err}`),
				});
				this._notifyRemoteUpdate();
			},
			error: (err) => {
				this.pendingSubscriptions.delete(key);
				this.onLog('error', `Subscriber attach: ${err}`);
			},
			onmessage: (msg, jsep) => {
				const entry = this.remoteFeeds.get(key);
				if (!entry) return;
				if (jsep) {
					// Recv-only subscription: omit tracks entirely so janus.js
					// doesn't try to capture or add a data channel — the answer
					// mirrors Janus's offer m-lines as-is.
					entry.handle.createAnswer({
						jsep,
						success: (answer) => {
							entry.handle.send({ message: { request: 'start' }, jsep: answer });
						},
						error: (err) => this.onLog('error', `Subscriber answer: ${err}`),
					});
				}
			},
			// Legacy path: older janus.js builds (or ones compiled without
			// multistream support) call onremotestream instead of onremotetrack.
			onremotestream: (stream) => {
				const entry = this.remoteFeeds.get(key);
				if (!entry || !stream) return;
				entry.stream = stream;
				stream.getTracks().forEach((t) => entry.tracksByMid.set(t.id, t));
				this.onLog('info', `Remote stream (legacy) for ${display}: ${stream.getTracks().length} track(s)`);
				this._notifyRemoteUpdate();
			},
			onremotetrack: (track, mid, on /*, metadata */) => {
				const entry = this.remoteFeeds.get(key);
				if (!entry) return;
				if (on) {
					const existing = entry.tracksByMid.get(mid);
					if (existing && existing.id === track.id) {
						// Same track signalled again (e.g. unmute) — nothing to do.
						return;
					}
					if (existing) {
						try {
							entry.stream.removeTrack(existing);
						} catch {
							/* ignore */
						}
					}
					entry.tracksByMid.set(mid, track);
					try {
						entry.stream.addTrack(track);
					} catch {
						/* ignore */
					}
					this.onLog('info', `Remote ${track.kind} track ready: ${display} (mid=${mid})`);
					this._notifyRemoteUpdate();
				} else {
					const existing = entry.tracksByMid.get(mid);
					if (existing) {
						try {
							entry.stream.removeTrack(existing);
						} catch {
							/* ignore */
						}
						entry.tracksByMid.delete(mid);
					}
					// Do NOT call track.stop() here. Janus reuses track ids across
					// unsubscribe/resubscribe cycles and stopping kills future
					// playback — see janus-gateway issue #3055.
					this._notifyRemoteUpdate();
				}
			},
			oncleanup: () => {
				// `oncleanup` fires when the PC closes / janus.js tears down. Only
				// forget this feed if the entry still belongs to *this* handle —
				// otherwise a late cleanup from a previous attach could nuke the
				// freshly re-subscribed entry for the same feed id.
				this.pendingSubscriptions.delete(key);
				const entry = this.remoteFeeds.get(key);
				if (entry && entry.handle === attachedHandle) {
					this.removeRemote(feedId);
				}
			},
		});
	}

	removeRemote (feedId) {
		const key = String(feedId);
		const entry = this.remoteFeeds.get(key);
		if (!entry) return;
		// Don't .stop() the remote tracks here either (see comment in
		// onremotetrack); just drop our references so the MediaStream can be
		// garbage collected with the entry.
		if (entry.tracksByMid) entry.tracksByMid.clear();
		try {
			if (entry.handle) entry.handle.detach();
		} catch {
			/* ignore */
		}
		this.remoteFeeds.delete(key);
		this._notifyRemoteUpdate();
	}

	_notifyRemoteUpdate () {
		if (!this.onRemoteStreams) return;
		const map = new Map();
		this.remoteFeeds.forEach((v, k) => {
			if (v.stream) map.set(k, { stream: v.stream, display: v.display });
		});
		this.onRemoteStreams(map);
	}

	getExamStream () {
		for (const [, v] of this.remoteFeeds) {
			if (v.display === CONSULT_DISPLAY_EXAM && v.stream) return v.stream;
		}
		return null;
	}

	getPatientStream () {
		for (const [, v] of this.remoteFeeds) {
			if (v.display === CONSULT_DISPLAY_PATIENT && v.stream) return v.stream;
		}
		return null;
	}
}
